import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {CustomPropertyValuesTable} from '@github-ui/custom-properties-editing'
import {
  propertyFieldsToChangedPropertiesMap,
  useEditCustomProperties,
} from '@github-ui/custom-properties-editing/use-edit-custom-properties'
import type {PropertyDefinition, PropertySourceDetails, Repository} from '@github-ui/custom-properties-types'
import {updateRepoPropertyValuesFromOrgPath, updateRepoPropertyValuesFromRepoPath} from '@github-ui/paths'
import sudo from '@github-ui/sudo'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {SearchIcon} from '@primer/octicons-react'
import {Button, TextInput, useConfirm} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {useEffect, useMemo, useState} from 'react'

import {useCurrentOrg, useCurrentRepo} from '../contexts/CurrentOrgRepoContext'
import {ServerErrorFormBanner} from './Banners'
import styles from './RepoPropertiesEditingPage.module.css'

const debounceAnnouncement = debounce(announce, 300)

export function RepoPropertiesEditingPage({
  editingRepos,
  onClose,
  editableProperties,
  definitions,
  onSuccess,
  renderPageHeader,
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  actionLabels = {save: 'Save', cancel: 'Cancel'},
  managedBySource,
}: {
  editableProperties: string[]
  editingRepos: Repository[]
  onClose: () => void
  definitions: PropertyDefinition[]
  onSuccess: () => void
  renderPageHeader: ({canClose}: {canClose: () => Promise<boolean>}) => React.ReactElement
  actionLabels?: {save?: string; cancel?: string}
  managedBySource?: Omit<PropertySourceDetails, 'avatarUrl'>
}) {
  const {propertyValuesMap, setPropertyValue, revertPropertyValue, discardChanges, commitChanges} =
    useEditCustomProperties(
      editingRepos.map(r => r.properties || {}),
      definitions,
    )
  const editableDefinitions = useMemo(
    () => definitions.filter(({propertyName}) => editableProperties.includes(propertyName)),
    [definitions, editableProperties],
  )

  const [submitting, setSubmitting] = useState(false)
  const [filterText, setFilterText] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const org = useCurrentOrg()
  const repository = useCurrentRepo()

  const confirm = useConfirm()

  const hasUnsavedChanges = useMemo(() => Object.values(propertyValuesMap).some(p => p.changed), [propertyValuesMap])
  useNavigateAwayDiscardConfirmation(hasUnsavedChanges)

  const canClose = async () => {
    if (hasUnsavedChanges) {
      return confirm({
        title: 'Discard changes?',
        content: 'You have unsaved changes. Are you sure you want to discard them?',
        cancelButtonContent: 'Keep editing',
        confirmButtonContent: 'Discard',
        confirmButtonType: 'danger',
      })
    } else {
      return true
    }
  }
  const discardWithConfirmation = async () => {
    if (hasUnsavedChanges) {
      if (await canClose()) {
        discardChanges()
        onClose()
      }
    } else {
      onClose()
    }
  }

  const onSubmit = async () => {
    setErrorMessage('')
    if (submitting) {
      return
    }

    if (Object.values(propertyValuesMap).some(p => !!p.error)) {
      return
    }

    setSubmitting(true)

    const changedProperties = propertyFieldsToChangedPropertiesMap(definitions, propertyValuesMap)
    if (Object.keys(changedProperties).length) {
      if (!(await sudo())) {
        setSubmitting(false)
        setErrorMessage('Unauthorized')
        return
      }

      const repoIds = editingRepos.map(repo => repo.id)
      try {
        const url = repository
          ? updateRepoPropertyValuesFromRepoPath({org: org.login, repo: repository.name})
          : updateRepoPropertyValuesFromOrgPath({org: org.login})

        const result = await verifiedFetchJSON(url, {
          method: 'POST',
          body: {
            repoIds,
            properties: changedProperties,
          },
        })
        if (result.ok) {
          commitChanges()
          onSuccess()
        } else {
          const {error} = await result.json()
          setErrorMessage(error)
        }
      } catch {
        setErrorMessage('Failed to update properties')
      }
    } else {
      onClose()
    }

    setSubmitting(false)
  }

  const filteredDefinitions = definitions.filter(({propertyName}) =>
    propertyName.toLowerCase().includes(filterText.toLowerCase()),
  )

  useEffect(() => {
    const resultsFound = filteredDefinitions.length
    if (resultsFound === 1) {
      debounceAnnouncement('1 result found')
    } else {
      debounceAnnouncement(`${resultsFound} results found`)
    }
  }, [filteredDefinitions])

  if (!editingRepos.length) {
    return null
  }

  return (
    <>
      {errorMessage && (
        <div className={styles.errorContainer}>
          <ServerErrorFormBanner>{errorMessage}</ServerErrorFormBanner>
        </div>
      )}
      {renderPageHeader({canClose})}
      <form data-testid="properties-editing-page" className={styles.propertiesEditingForm}>
        <TextInput
          block
          leadingVisual={SearchIcon}
          placeholder="Filter properties"
          aria-label="Filter properties"
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
        />

        {filteredDefinitions.length === 0 ? (
          <Blankslate border>
            <Blankslate.Heading>No properties that match</Blankslate.Heading>
          </Blankslate>
        ) : (
          <CustomPropertyValuesTable
            definitions={filteredDefinitions}
            editableDefinitions={editableDefinitions}
            propertyValuesMap={propertyValuesMap}
            setPropertyValue={setPropertyValue}
            revertPropertyValue={revertPropertyValue}
            managedBySource={managedBySource}
          />
        )}

        <div className={styles.formActionsContainer}>
          {actionLabels.save && (
            <Button type="button" onClick={onSubmit} variant="primary">
              {actionLabels.save}
            </Button>
          )}

          {actionLabels.cancel && (
            <Button type="button" onClick={discardWithConfirmation} variant="default">
              {actionLabels.cancel}
            </Button>
          )}
        </div>
      </form>
    </>
  )
}

function useNavigateAwayDiscardConfirmation(hasUnsavedChanges: boolean) {
  useEffect(() => {
    document.body.setAttribute('data-turbo', 'false')
    window.onbeforeunload = hasUnsavedChanges
      ? e => {
          e.returnValue = 'You have unsaved changes. Are you sure you want to leave this page?'
        }
      : null

    return () => {
      document.body.removeAttribute('data-turbo')
      window.onbeforeunload = null
    }
  }, [hasUnsavedChanges])
}

try{ RepoPropertiesEditingPage.displayName ||= 'RepoPropertiesEditingPage' } catch {}